import * as React from 'react';
import {
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  IconButton,
  MenuItem,
  FormControl,
  Select,
  SelectChangeEvent,
  Typography,
  ListItemText,
  Checkbox,
  OutlinedInput,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import CreateButtonWithModal from '../components/common/CreateButtonWithModal';
import BlackWhiteSwitch from '../components/common/BlackWhiteSwitch';
import DeleteButton from '../components/common/DeleteButton';
import EditModal from '../components/common/EditModal';
import Tooltip from '../components/common/Tooltip';

interface Column {
  id: keyof Data | 'action';
  label: string;
  minWidth?: number;
  align?: 'right' | 'center' | 'left';
}

interface Data {
  ID: string;
  name: string;
  services: string;
  contactperson: string;
  contactemail: string;
  contactphone: string;
  'website URL': string;
  'API based URL': string;
  'Support booking': string;
  'Support cancellation': string;
  status: string;
  createdBy: string;
}

const columns: readonly Column[] = [
  { id: 'name', label: 'Name', minWidth: 150 },
  { id: 'ID', label: 'ID', minWidth: 60 },
  { id: 'services', label: 'Services', minWidth: 120 },
  { id: 'contactperson', label: 'Contact Person', minWidth: 150 },
  { id: 'contactemail', label: 'Contact Email', minWidth: 200 },
  { id: 'contactphone', label: 'Contact Phone', minWidth: 150 },
  { id: 'website URL', label: 'Website URL', minWidth: 200 },
  { id: 'API based URL', label: 'API URL', minWidth: 200 },
  { id: 'Support booking', label: 'Supports Booking', minWidth: 150 },
  { id: 'Support cancellation', label: 'Supports Cancellation', minWidth: 180 },
  { id: 'status', label: 'Status', minWidth: 100 },
  { id: 'createdBy', label: 'Created By', minWidth: 120 },
  { id: 'action', label: 'Action', minWidth: 100, align: 'center' },
];

function createData(
  name: string,
  ID: string,
  services: string,
  contactperson: string,
  contactemail: string,
  contactphone: string,
  websiteURL: string,
  apiURL: string,
  supportBooking: string,
  supportCancellation: string,
  status: string,
  createdBy: string
): Data {
  return {
    name,
    ID,
    services,
    contactperson,
    contactemail,
    contactphone,
    'website URL': websiteURL,
    'API based URL': apiURL,
    'Support booking': supportBooking,
    'Support cancellation': supportCancellation,
    status,
    createdBy,
  };
}

const initialRows: Data[] = [
  createData('SUP001', 'Supplier A', 'Flights, Hotels', 'Alice', 'alice@suppliera.com', '+1-123-456-7890', 'https://www.suppliera.com', 'https://api.suppliera.com', 'Yes', 'Yes', 'Active', 'Admin'),
  createData('SUP002', 'Supplier B', 'Transfers', 'Bob', 'bob@supplierb.com', '+44-789-654-3210', 'https://www.supplierb.com', 'https://api.supplierb.com', 'Yes', 'No', 'Inactive', 'John'),
  createData('SUP003', 'Supplier C', 'Activities', 'Charlie', 'charlie@supplierc.com', '+91-98765-43210', 'https://www.supplierc.com', 'https://api.supplierc.com', 'No', 'Yes', 'Active', 'Emma'),
];

const ITEM_HEIGHT = 48;
  const ITEM_PADDING_TOP = 8;
  const MenuProp = {
    PaperProps: {
      style: {
        maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
        width: 250,
      },
    },
    anchorOrigin: {
      vertical: 'bottom' as 'bottom',
      horizontal: 'left' as 'left',
    },
    transformOrigin: {
      vertical: 'top' as 'top',
      horizontal: 'left' as 'left',
    },
    getContentAnchorEl: null, // Important for older versions of MUI v4; can omit for MUI v5+
  };

const label = [
  'Name',
  'ID',
  'Services',
  'Contact Person',
  'Contact Email',
  'Contact Phone',
  'Website URL',
  'API Based URL',
  'Support Booking',
  'Support Cancellation',
];

const StaySuppliers = () => {
  const [rows, setRows] = React.useState<Data[]>(initialRows);
  const [filteredRows, setFilteredRows] = React.useState<Data[]>(initialRows);
  const [editOpen, setEditOpen] = React.useState(false);
  const [selectedRowIndex, setSelectedRowIndex] = React.useState<number | null>(null);
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(10);
  const [searchTerm, setSearchTerm] = React.useState('');
  const [filterBy, setFilterBy] = React.useState('');

  const handleChangePage = (_: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  const handleFilterChange = (event: SelectChangeEvent) => {
    setFilterBy(event.target.value);
  };

  const handleSearch = () => {
    if (!filterBy || !searchTerm) {
      setFilteredRows(rows);
      return;
    }

    const lowerSearch = searchTerm.toLowerCase();
    const filtered = rows.filter((row) =>
      String(row[filterBy as keyof Data]).toLowerCase().includes(lowerSearch)
    );
    setFilteredRows(filtered);
  };

  const handleEditSave = (updatedData: Data) => {
    if (selectedRowIndex === null) return;
    const updatedRows = [...rows];
    updatedRows[selectedRowIndex] = updatedData;
    setRows(updatedRows);
    setFilteredRows(updatedRows);
    setEditOpen(false);
  };

  const handleDeleteRow = (id: string) => {
    const updatedRows = rows.filter((row) => row.ID !== id);
    setRows(updatedRows);
    setFilteredRows(updatedRows);
  };

  const renderStatusButton = (status: string) => (
    <Button
      variant="contained"
      size="small"
      sx={{
        backgroundColor: status === 'Active' ? '#1cad2d' : '#e31a24',
        color: 'white',
        fontWeight: 'bold',
        textTransform: 'none',
        '&:hover': {
          backgroundColor: status === 'Active' ? '#2e7d32' : '#c62828',
        },
      }}
    >
      {status}
    </Button>
  );

  const renderActions = (row: Data, index: number) => (
    <>
      <IconButton
        color="primary"
        size="small"
        onClick={() => {
          setSelectedRowIndex(index);
          setEditOpen(true);
        }}
      >
        <EditIcon />
      </IconButton>
      <DeleteButton
        onDelete={() => handleDeleteRow(row.ID)}
        confirmMessage="Do you really want to delete this item?"
      />
    </>
  );

  React.useEffect(() => {
    setFilteredRows(rows);
  }, [rows]);

  const [labelName, setLabelName] = React.useState<string[]>([]);
      
        const labelChange = (event: SelectChangeEvent<typeof labelName>) => {
          const {
            target: { value },
          } = event;
          setLabelName(
            // On autofill we get a stringified value.
            typeof value === 'string' ? value.split(',') : value,
          );
        };

  return (
    <>
      <Box className="headingBtnWrap" >
            <Typography variant="h1">Supplier Details</Typography>
              <CreateButtonWithModal modalTitle="Suppliers">
                <Box className="modalFormWrap">
                  <Box component="form" className="modalForm">
                    <div className="form-control inputFieldWrap">
                      <label>Name</label>
                      <input type="text" className="inputField" />
                    </div>
                    <div className="form-control inputFieldWrap">
                      <label>ID</label>
                      <input type="text" className="inputField" />
                    </div>
                    <div className="form-control inputFieldWrap">
                      <label>Services</label>
                      <input type="text" className="inputField" />
                    </div>
                    <div className="form-control inputFieldWrap">
                      <label>Contact Person</label>
                      <input type="text" className="inputField" />
                    </div>
                    <div className="form-control inputFieldWrap">
                      <label>Contact Email</label>
                      <input type="text" className="inputField" />
                    </div>
                    <div className="form-control inputFieldWrap">
                      <label>Contact Phone</label>
                      <input type="text" className="inputField" />
                    </div>
                  </Box>
                  <Box display="flex" justifyContent="flex-end">
                    <Button variant="contained" className="formBtn">
                      Save
                    </Button>
                  </Box>
                </Box>
                <BlackWhiteSwitch />
              </CreateButtonWithModal>
            </Box>

      <Box className="searchInputWrap">
            <Box
              display="flex"
              alignItems="center"
              gap={2}
              p={2}
              sx={{ width: '100%'}}
              className="searchInputFilterBox"
            >
              <FormControl sx={{ minWidth: 460 }}>
                 <Select
                  className='leftFilterBox'
                  labelId="demo-multiple-checkbox-label"
                  id="demo-multiple-checkbox"
                  multiple
                  value={labelName}
                  onChange={labelChange}
                  displayEmpty
                  input={<OutlinedInput />}
                  renderValue={(selected) => {
                    if (selected.length === 0) {
                      return 'Select';  // <-- Placeholder text
                    }
                    return selected.join(', ');
                  }}
                  MenuProps={MenuProp}
                >
                  <MenuItem disabled value="">
                  Select
                  </MenuItem>
                  {label.map((name) => (
                    <MenuItem key={name} value={name} className='selectDropdown'>
                      <Checkbox checked={labelName.includes(name)} />
                      <ListItemText primary={name} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Button
                      variant="contained"
                      className="searchBtn"
                      color="primary"
                      type="submit"
                      sx={{ borderTopLeftRadius: 0, borderBottomLeftRadius: 0 }}
                    >
                      Search
                    </Button>
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }} className="dynamicInputFieldWrap">
              {labelName.map((item) => (
                <div key={item} className="form-control inputFieldWrap">
                  <label style={{ marginBottom: '4px', fontWeight: '500' }}>{item}</label>
                  <input
                    type="text"
                    // placeholder={`Enter ${item}`}
                    className="inputField"
                  />
                </div>
              ))}
            </Box>
          </Box>

      <Paper sx={{ width: '100%', overflowX: 'auto' }}>
        <TableContainer sx={{ maxHeight: 440 }}>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                {columns.map((column) => (
                  <TableCell
                    key={column.id}
                    align={column.align || 'left'}
                    style={{ minWidth: column.minWidth }}
                  >
                    {column.label}
                  </TableCell>
                ))}
                <TableCell sx={{ minWidth: 100 }} align="center">Audit Trail</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredRows
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((row, rowIndex) => (
                  <TableRow hover key={row.ID}>
                    {columns.map((column) => {
                      if (column.id === 'status') {
                        return (
                          <TableCell key={column.id}>
                            {renderStatusButton(row.status)}
                          </TableCell>
                        );
                      }
                      if (column.id === 'action') {
                        return (
                          <TableCell key={column.id} align="center">
                            {renderActions(row, rowIndex)}
                          </TableCell>
                        );
                      }
                      return (
                        <TableCell key={column.id}>
                          {row[column.id as keyof Data]}
                        </TableCell>
                      );
                    })}
                    <TableCell align="center">
                                        <Tooltip />
                                      </TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[10, 25, 100]}
          component="div"
          count={filteredRows.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Paper>

      {selectedRowIndex !== null && (
        <EditModal
          open={editOpen}
          onClose={() => setEditOpen(false)}
          initialData={rows[selectedRowIndex]}
          onSave={handleEditSave}
          excludeFields={['action', 'status']}
          readOnlyFields={['createdBy']}
          title="Edit Suppliers"
        />
      )}
    </>
  );
};

export default StaySuppliers;
